import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "./axios";

// Update Trader Kassa Management Permission
export const updateTraderKassaPermission = createAsyncThunk(
  "traderKassaManagement/updatePermission",
  async ({ userId, enabled, authorizedTraders }, { rejectWithValue }) => {
    try {
      const response = await axios.patch("/admin/updateTraderKassaManagement", {
        userId,
        enabled,
        authorizedTraders,
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update permission"
      );
    }
  }
);

// Fetch Authorized Traders
export const fetchAuthorizedTraders = createAsyncThunk(
  "traderKassaManagement/fetchAuthorizedTraders",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        "/admin/trader-kassa-management/traders"
      );
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch traders"
      );
    }
  }
);

// Fetch Trader Detail
export const fetchTraderDetail = createAsyncThunk(
  "traderKassaManagement/fetchTraderDetail",
  async (traderName, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `/admin/trader-kassa-management/traders/${traderName}`
      );
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch trader detail"
      );
    }
  }
);

// Update Trader Commissions
export const updateTraderCommissions = createAsyncThunk(
  "traderKassaManagement/updateCommissions",
  async (
    { traderName, depositCommission, withdrawalCommission },
    { rejectWithValue }
  ) => {
    try {
      const response = await axios.patch(
        `/admin/trader-kassa-management/traders/${traderName}/commissions`,
        {
          depositCommission,
          withdrawalCommission,
        }
      );
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update commissions"
      );
    }
  }
);

// Fetch Crypto Deposits
export const fetchCryptoDeposits = createAsyncThunk(
  "traderKassaManagement/fetchCryptoDeposits",
  async ({ traderName, status, limit = 50, page = 1 }, { rejectWithValue }) => {
    try {
      let url = `/admin/trader-kassa-management/traders/${traderName}/crypto-deposits?limit=${limit}&page=${page}`;
      if (status) {
        url += `&status=${status}`;
      }
      const response = await axios.get(url);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch crypto deposits"
      );
    }
  }
);

// Fetch Balance History
export const fetchBalanceHistory = createAsyncThunk(
  "traderKassaManagement/fetchBalanceHistory",
  async ({ traderName, type, limit = 50, page = 1 }, { rejectWithValue }) => {
    try {
      let url = `/admin/trader-kassa-management/traders/${traderName}/balance-history?limit=${limit}&page=${page}`;
      if (type) {
        url += `&type=${type}`;
      }
      const response = await axios.get(url);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch balance history"
      );
    }
  }
);

// Fetch Panel Deposit Stats
export const fetchPanelDepositStats = createAsyncThunk(
  "traderKassaManagement/fetchPanelDepositStats",
  async ({ traderName, startDate, endDate }, { rejectWithValue }) => {
    try {
      let url = `/admin/trader-kassa-management/traders/${traderName}/panel-deposit-stats`;
      const params = [];
      if (startDate) params.push(`startDate=${startDate}`);
      if (endDate) params.push(`endDate=${endDate}`);
      if (params.length > 0) {
        url += `?${params.join("&")}`;
      }
      const response = await axios.get(url);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch deposit stats"
      );
    }
  }
);

// Fetch Panel Withdrawal Stats
export const fetchPanelWithdrawalStats = createAsyncThunk(
  "traderKassaManagement/fetchPanelWithdrawalStats",
  async ({ traderName, startDate, endDate }, { rejectWithValue }) => {
    try {
      let url = `/admin/trader-kassa-management/traders/${traderName}/panel-withdrawal-stats`;
      const params = [];
      if (startDate) params.push(`startDate=${startDate}`);
      if (endDate) params.push(`endDate=${endDate}`);
      if (params.length > 0) {
        url += `?${params.join("&")}`;
      }
      const response = await axios.get(url);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch withdrawal stats"
      );
    }
  }
);

// Fetch Pending Trader Deposits
export const fetchPendingTraderDeposits = createAsyncThunk(
  "traderKassaManagement/fetchPendingDeposits",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get("/admin/trader-deposits/pending");
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch pending deposits"
      );
    }
  }
);

// Approve Trader Deposit
export const approveTraderDeposit = createAsyncThunk(
  "traderKassaManagement/approveDeposit",
  async ({ depositId, exchangeRate, notes }, { rejectWithValue }) => {
    try {
      const response = await axios.post("/admin/trader-deposits/approve", {
        depositId,
        exchangeRate,
        notes,
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to approve deposit"
      );
    }
  }
);

// Reject Trader Deposit
export const rejectTraderDeposit = createAsyncThunk(
  "traderKassaManagement/rejectDeposit",
  async ({ depositId, reason }, { rejectWithValue }) => {
    try {
      const response = await axios.post("/admin/trader-deposits/reject", {
        depositId,
        reason,
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to reject deposit"
      );
    }
  }
);

// Update Trader TRC Address
export const updateTraderTrcAddress = createAsyncThunk(
  "traderKassaManagement/updateTrcAddress",
  async (
    { traderName, trcWalletAddress, trcWalletNetwork },
    { rejectWithValue }
  ) => {
    try {
      const response = await axios.patch("/admin/trader-trc-address", {
        traderName,
        trcWalletAddress,
        trcWalletNetwork,
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update TRC address"
      );
    }
  }
);

// Get Trader TRC Address
export const getTraderTrcAddress = createAsyncThunk(
  "traderKassaManagement/getTrcAddress",
  async (traderName, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `/admin/trader-trc-address/${traderName}`
      );
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch TRC address"
      );
    }
  }
);

// Fetch Pending Trader Withdrawals
export const fetchPendingTraderWithdrawals = createAsyncThunk(
  "traderKassaManagement/fetchPendingWithdrawals",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get("/admin/trader-withdrawals/pending");
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch pending withdrawals"
      );
    }
  }
);

// Approve Trader Withdrawal
export const approveTraderWithdrawal = createAsyncThunk(
  "traderKassaManagement/approveWithdrawal",
  async ({ withdrawalId, exchangeRate, notes, txId }, { rejectWithValue }) => {
    try {
      const response = await axios.post("/admin/trader-withdrawals/approve", {
        withdrawalId,
        exchangeRate,
        notes,
        txId,
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to approve withdrawal"
      );
    }
  }
);

// Reject Trader Withdrawal
export const rejectTraderWithdrawal = createAsyncThunk(
  "traderKassaManagement/rejectWithdrawal",
  async ({ withdrawalId, reason }, { rejectWithValue }) => {
    try {
      const response = await axios.post("/admin/trader-withdrawals/reject", {
        withdrawalId,
        reason,
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to reject withdrawal"
      );
    }
  }
);

// Fetch Trader Withdrawals (Admin)
export const fetchAdminTraderWithdrawals = createAsyncThunk(
  "traderKassaManagement/fetchWithdrawals",
  async (
    { traderName, status = "", limit = 50, page = 1 },
    { rejectWithValue }
  ) => {
    try {
      const response = await axios.get(
        `/admin/trader-kassa-management/traders/${traderName}/withdrawals`,
        { params: { status, limit, page } }
      );
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch withdrawals"
      );
    }
  }
);
